import { StrictExtract } from 'common/utilities/TypeHelpers';
import { Alert, EventType, SettingDetails, ThreatDetails, UnsafeUrlDetails } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';

export interface IAlerts {
  alertIds: string[];
  fetchPageApiStatus: ApiCallResultStatus;
  fetchPageError: string | undefined;
  paginationData: IPaginationData | undefined;
}

export interface IPaginationData {
  totalItemCount: number;
  totalActiveItemCount: number;
  totalInactiveItemCount: number;
  activeItemCount: number;
  loadedPages: number;
  loadedItemsCount: number;
  pendingItemsCount: number;
}

export interface ISecureConnectionAlertBody {
  vpnBandwidthConsumptionPercentage: string | undefined;
  vpnRemainingBandwidthInGB: string | undefined;
  vpnMaxBandwidthInGB: string | undefined;
}

export const deviceAutoSampleSubmissionEventTypes = ['AutoSampleSubmissionSetting'] as const;
export const deviceAntiphishingSettingEventTypes = ['AntiphishingOff', 'AntiphishingOn', 'AntiphishingSetting'] as const;
export const deviceRtpSettingEventTypes = ['DeviceRtpOff', 'DeviceRtpOn', 'DeviceRtpSetting'] as const;
export const deviceSettingEventTypes = [
  ...deviceAntiphishingSettingEventTypes,
  ...deviceRtpSettingEventTypes,
  ...deviceAutoSampleSubmissionEventTypes,
] as const;
export const deviceThreatEventTypes = ['DeviceThreat', 'DeviceThreatDetected', 'DeviceThreatResolved'] as const;
export const deviceUnsafeUrlEventTypes = ['PhishingLink'] as const;
export const secureConnectionEventTypes = ['SecureConnectionConsumerVpnMonthlyQuota'] as const;

export type DeviceAutoSampleSubmissionEventTypes = StrictExtract<EventType, (typeof deviceAutoSampleSubmissionEventTypes)[number]>;
export type DeviceAntiphishingSettingEventTypes = StrictExtract<EventType, (typeof deviceAntiphishingSettingEventTypes)[number]>;
export type DeviceRtpSettingEventTypes = StrictExtract<EventType, (typeof deviceRtpSettingEventTypes)[number]>;
export type DeviceSettingEventTypes = StrictExtract<EventType, (typeof deviceSettingEventTypes)[number]>;
export type DeviceThreatEventTypes = StrictExtract<EventType, (typeof deviceThreatEventTypes)[number]>;
export type DeviceUnsafeUrlEventTypes = StrictExtract<EventType, (typeof deviceUnsafeUrlEventTypes)[number]>;
export type SecureConnectionVpnMonthlyQuotaEventTypes = StrictExtract<EventType, (typeof secureConnectionEventTypes)[number]>;
export type DeviceEventTypes = DeviceSettingEventTypes | DeviceThreatEventTypes | DeviceUnsafeUrlEventTypes;

export type IAlertDetails = Alert & {
  dashboardData?: IDashboardAlertData;
  fetchAlertApiStatus: ApiCallResultStatus;
  fetchAlertError: string | undefined;
  updateAlertsApiStatus: ApiCallResultStatus;
  updateAlertsError: string | undefined;
} & (
    | {
        eventType: DeviceSettingEventTypes;
        /**
         * More details for setting device events
         */
        deviceAlertDetails?: SettingDetails;
      }
    | {
        eventType: DeviceThreatEventTypes;
        /**
         * More details for threat device events
         */
        deviceAlertDetails?: ThreatDetails;
      }
    | {
        eventType: DeviceUnsafeUrlEventTypes;
        /**
         * More details for unsafeUrl device events
         */
        deviceAlertDetails?: UnsafeUrlDetails;
      }
    | {
        eventType: Exclude<EventType, DeviceEventTypes>;
        deviceAlertDetails?: undefined;
      }
  );

export interface IDashboardAlertData {
  title?: string;
  subtitle?: string;
}
